import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { keyFromName, nativeSelectClass } from "@/tenax/tasks/taskData";
import { useKitVersions, usePackages, type Package } from "./agreementData";

// Packages and kit versions, for the admin (task 1.10, D-039), inside the
// settings form: no nested form, Enter does not submit the settings. A
// package or kit in use is renamed or turned off; the database refuses to
// delete it, so there is no delete button.
const useRun = (resource: string) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  return async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: [resource] });
  };
};

const blockEnter = (event: React.KeyboardEvent) => {
  if (event.key === "Enter") event.preventDefault();
};

export const PackagesSettings = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const run = useRun("packages");
  const { data: packages = [] } = usePackages();
  const { data: kits = [] } = useKitVersions();
  const [newName, setNewName] = useState("");

  const update = (pkg: Package, data: Partial<Package>) =>
    run(() =>
      dataProvider.update("packages", { id: pkg.id, data, previousData: pkg }),
    );
  const numberOrNull = (value: string) => (value === "" ? null : Number(value));

  return (
    <section
      aria-label={translate("tenax.agreements.packages.title")}
      className="flex flex-col gap-3"
    >
      <h3 className="text-lg font-semibold">
        {translate("tenax.agreements.packages.title")}
      </h3>
      <p className="text-sm text-muted-foreground">
        {translate("tenax.agreements.packages.hint")}
      </p>
      <ul className="flex flex-col gap-3">
        {packages.map((pkg) => (
          <li
            key={pkg.id}
            aria-label={pkg.name}
            className="grid grid-cols-2 gap-2 rounded-md border p-2 md:grid-cols-6"
          >
            <Input
              aria-label={translate("resources.agreements.fields.package_id")}
              defaultValue={pkg.name}
              onKeyDown={blockEnter}
              onBlur={(event) =>
                event.target.value.trim() && event.target.value !== pkg.name
                  ? update(pkg, { name: event.target.value.trim() })
                  : undefined
              }
              className="md:col-span-2"
            />
            <Input
              type="number"
              aria-label={translate(
                "resources.agreements.fields.contribution_amount",
              )}
              placeholder={translate(
                "resources.agreements.fields.contribution_amount",
              )}
              defaultValue={pkg.contribution_amount ?? ""}
              onKeyDown={blockEnter}
              onBlur={(event) =>
                update(pkg, {
                  contribution_amount: numberOrNull(event.target.value),
                })
              }
            />
            <select
              aria-label={translate(
                "resources.agreements.fields.kit_version_id",
              )}
              className={nativeSelectClass}
              value={pkg.kit_version_id ?? ""}
              onChange={(event) =>
                update(pkg, {
                  kit_version_id: numberOrNull(event.target.value),
                })
              }
            >
              <option value="">—</option>
              {kits.map((kit) => (
                <option key={kit.id} value={kit.id}>
                  {kit.name}
                </option>
              ))}
            </select>
            <Input
              type="number"
              aria-label={translate(
                "resources.agreements.fields.customer_discount_pct",
              )}
              placeholder={translate(
                "resources.agreements.fields.customer_discount_pct",
              )}
              defaultValue={pkg.customer_discount_pct ?? ""}
              onKeyDown={blockEnter}
              onBlur={(event) =>
                update(pkg, {
                  customer_discount_pct: numberOrNull(event.target.value),
                })
              }
            />
            <Input
              type="number"
              aria-label={translate(
                "resources.agreements.fields.referrer_reward_pct",
              )}
              placeholder={translate(
                "resources.agreements.fields.referrer_reward_pct",
              )}
              defaultValue={pkg.referrer_reward_pct ?? ""}
              onKeyDown={blockEnter}
              onBlur={(event) =>
                update(pkg, {
                  referrer_reward_pct: numberOrNull(event.target.value),
                })
              }
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => update(pkg, { active: !pkg.active })}
            >
              {translate(
                pkg.active
                  ? "tenax.agreements.packages.turn_off"
                  : "tenax.agreements.packages.turn_on",
              )}
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <Label htmlFor="tenax-new-package">
            {translate("tenax.agreements.packages.new")}
          </Label>
          <Input
            id="tenax-new-package"
            value={newName}
            onKeyDown={blockEnter}
            onChange={(event) => setNewName(event.target.value)}
          />
        </div>
        <Button
          type="button"
          disabled={!newName.trim()}
          onClick={() =>
            run(async () => {
              await dataProvider.create("packages", {
                data: {
                  key: keyFromName(newName),
                  name: newName.trim(),
                  position: (packages.at(-1)?.position ?? 0) + 1,
                },
              });
              setNewName("");
            })
          }
        >
          {translate("tenax.agreements.packages.add")}
        </Button>
      </div>
    </section>
  );
};

export const KitVersionsSettings = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const run = useRun("kit_versions");
  const { data: kits = [] } = useKitVersions();
  const [newName, setNewName] = useState("");

  return (
    <section
      aria-label={translate("tenax.agreements.kits.title")}
      className="flex flex-col gap-3"
    >
      <h3 className="text-lg font-semibold">
        {translate("tenax.agreements.kits.title")}
      </h3>
      <ul className="flex flex-col gap-2">
        {kits.map((kit) => (
          <li
            key={kit.id}
            aria-label={kit.name}
            className="flex items-center gap-2"
          >
            <Input
              aria-label={translate(
                "resources.agreements.fields.kit_version_id",
              )}
              defaultValue={kit.name}
              onKeyDown={blockEnter}
              onBlur={(event) =>
                event.target.value.trim() && event.target.value !== kit.name
                  ? run(() =>
                      dataProvider.update("kit_versions", {
                        id: kit.id,
                        data: { name: event.target.value.trim() },
                        previousData: kit,
                      }),
                    )
                  : undefined
              }
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                run(() =>
                  dataProvider.update("kit_versions", {
                    id: kit.id,
                    data: { active: !kit.active },
                    previousData: kit,
                  }),
                )
              }
            >
              {translate(
                kit.active
                  ? "tenax.agreements.kits.turn_off"
                  : "tenax.agreements.kits.turn_on",
              )}
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <Label htmlFor="tenax-new-kit">
            {translate("tenax.agreements.kits.new")}
          </Label>
          <Input
            id="tenax-new-kit"
            value={newName}
            onKeyDown={blockEnter}
            onChange={(event) => setNewName(event.target.value)}
          />
        </div>
        <Button
          type="button"
          disabled={!newName.trim()}
          onClick={() =>
            run(async () => {
              await dataProvider.create("kit_versions", {
                data: { key: keyFromName(newName), name: newName.trim() },
              });
              setNewName("");
            })
          }
        >
          {translate("tenax.agreements.kits.add")}
        </Button>
      </div>
    </section>
  );
};
